import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ChevronLeft } from 'lucide-react';
import { useTranslation } from '@/components/utils/translations';
import { AccountFinal, PersonFinal } from '@/api/entities';
import { useUser } from '@/contexts/UserContext';
import { resolveEffectiveTenant } from '@/lib/tenantUtils';
import { addPlanItemToAckSet, limitTodaysPlanForDisplay } from '@/lib/todaysPlanUtils';
import { notify } from '@/components/ui/notify';
import { createPageUrl } from '@/utils';
import usePilotDeals from '@/components/home/pilot/usePilotDeals';
import TasksTile from '@/components/home/pilot/TasksTile';
import { acknowledgePlanItem, readAckSet } from '@/components/home/pilot/planAck';

const API_BASE_URL = 'https://api.neogtm.com';
const BACK_CLASS = 'inline-flex items-center gap-2 text-sm font-medium text-[#5C6B7A] hover:text-[#0A1A2F]';

/**
 * Every task, not the Home tile's top five (2026-11-04: "tasks doesnt have a link to its own page").
 * Same rows, rules and actions as the Home Tasks tile — deal rules from usePilotDeals, outreach
 * due from /todays-plan/tasks, then Today's plan items not yet marked done.
 */
export default function Tasks({ currentTenant }) {
  const contextUser = useUser();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const userId = contextUser?.id || null;
  const tenantId = useMemo(() => {
    const adminTenant = contextUser?.role === 'admin' ? localStorage.getItem('admin-active-tenant') : null;
    return resolveEffectiveTenant(adminTenant, currentTenant, contextUser, 'instructure');
  }, [currentTenant, contextUser]);

  const deals = usePilotDeals(tenantId);
  const [planData, setPlanData] = useState(null);
  const [planLoading, setPlanLoading] = useState(true);
  const [people, setPeople] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [acked, setAcked] = useState(() => readAckSet(userId));

  useEffect(() => {
    if (!tenantId || !userId) return undefined;
    let cancelled = false;
    setAcked(readAckSet(userId));
    const fetchPlan = () => fetch(`${API_BASE_URL}/api/todays-plan?tenant_id=${encodeURIComponent(tenantId)}&user_id=${encodeURIComponent(userId)}`, {
      headers: { 'Content-Type': 'application/json', 'x-user-id': userId, 'x-tenant-id': tenantId },
      credentials: 'include',
    });
    setPlanLoading(true);
    (async () => {
      try {
        let res = await fetchPlan();
        // Same 429 handling as Home: the route refuses a repeat within 2 s (e.g. Home -> Tasks).
        for (let attempt = 0; res.status === 429 && attempt < 2; attempt++) {
          const body = await res.json().catch(() => ({}));
          await new Promise((r) => setTimeout(r, Math.min(10, Number(body?.retryAfter) || 2) * 1000 + 250));
          res = await fetchPlan();
        }
        const data = res.ok ? await res.json() : null;
        if (!cancelled) setPlanData(data);
      } catch {
        /* keep whatever is on screen */
      } finally {
        if (!cancelled) setPlanLoading(false);
      }
    })();
    PersonFinal.filter({ tenant_id: tenantId }, '-persona_fit_score', 500)
      .then((rows) => !cancelled && setPeople(rows || []))
      .catch(() => {});
    AccountFinal.filter({ tenant_id: tenantId }, '-account_fit_score', 500)
      .then((rows) => !cancelled && setAccounts(rows || []))
      .catch(() => {});
    return () => { cancelled = true; };
  }, [tenantId, userId]);

  // Same selection and order as the Home tile (highest score first, research items last), minus done.
  const openItems = useMemo(
    () => Object.values(limitTodaysPlanForDisplay(planData, acked, { outreachLimit: 1000 })).flat(),
    [planData, acked]
  );

  const onAcknowledge = useCallback(async (item) => {
    setAcked((s) => addPlanItemToAckSet(s, item));
    try {
      await acknowledgePlanItem({ tenantId, userId, item });
    } catch (e) {
      notify({ title: 'Saved on this device only', description: e?.message, variant: 'default' });
    }
  }, [tenantId, userId]);

  // A due touch's draft opens from the Outbox, where the full drawer lives.
  const onOpenDraft = useCallback(() => navigate(createPageUrl('Outbox')), [navigate]);

  return (
    <div className="bg-gray-50 -m-8 -mt-8 min-h-screen">
      <div className="bg-white border-b mb-4 -mx-8">
        <div className="max-w-7xl mx-auto px-12 sm:px-8 md:px-10 py-5">
          {/* Same Back as PersonProfile: history back when there is an in-app page behind this one,
              otherwise a link that says where it goes. */}
          {typeof window !== 'undefined' && typeof window.history?.state?.idx === 'number' && window.history.state.idx > 0 ? (
            <button type="button" onClick={() => navigate(-1)} className={BACK_CLASS}>
              <ChevronLeft className="w-4 h-4" aria-hidden="true" />
              {t('back')}
            </button>
          ) : (
            <Link to={createPageUrl('Home')} className={BACK_CLASS}>
              <ChevronLeft className="w-4 h-4" aria-hidden="true" />
              Back to Home
            </Link>
          )}
          <h1 className="mt-2 text-2xl font-bold text-[#0A1A2F]">Tasks</h1>
        </div>
      </div>
      <div className="max-w-7xl mx-auto px-2 sm:px-6 pb-10">
        <TasksTile
          full
          planLoading={planLoading}
          tenantId={tenantId}
          userId={userId}
          deals={deals}
          plan={openItems}
          people={people}
          accounts={accounts}
          onOpenDraft={onOpenDraft}
          onAcknowledge={onAcknowledge}
        />
      </div>
    </div>
  );
}
